import type { MeasurementUnit } from '@/types/api.types';

// All available measurement units
export const MEASUREMENT_UNITS: MeasurementUnit[] = ['g', 'kg', 'ml', 'cl', 'l', 'unit'];

// Unit groups - units within the same group are compatible
export const UNIT_GROUPS = {
  weight: ['g', 'kg'] as const,
  volume: ['ml', 'cl', 'l'] as const,
  count: ['unit'] as const,
} as const;

// Fraction options for unit-based ingredients
export const FRACTION_UNITS = [
  { value: 0.2, label: '1/5' },
  { value: 0.4, label: '2/5' },
  { value: 0.6, label: '3/5' },
  { value: 0.8, label: '4/5' },
  { value: 1, label: '5/5 (1)' },
  { value: 1.2, label: '6/5' },
  { value: 1.4, label: '7/5' },
  { value: 1.6, label: '8/5' },
  { value: 1.8, label: '9/5' },
  { value: 2, label: '10/5 (2)' },
];

// Get compatible units for a given unit
export function getCompatibleUnits(unit: MeasurementUnit): MeasurementUnit[] {
  for (const [_, units] of Object.entries(UNIT_GROUPS)) {
    const unitsArray = Array.from(units) as MeasurementUnit[]
    if (unitsArray.includes(unit)) {
      return unitsArray
    }
  }
  return [unit];
}

// Check if two units are compatible
export function areUnitsCompatible(unit1: MeasurementUnit, unit2: MeasurementUnit): boolean {
  const compatible1 = getCompatibleUnits(unit1);
  return compatible1.includes(unit2);
}

// Get unit group name
export function getUnitGroup(unit: MeasurementUnit): string {
  for (const [group, units] of Object.entries(UNIT_GROUPS)) {
    const unitsArray = Array.from(units) as MeasurementUnit[]
    if (unitsArray.includes(unit)) {
      return group;
    }
  }
  return 'other';
}

// Normalize quantity to base unit (g for weight, ml for volume, unit for count)
export function normalizeToBaseUnit(quantity: number, unit: MeasurementUnit): { quantity: number; unit: MeasurementUnit } {
  switch (unit) {
    case 'kg':
      return { quantity: quantity * 1000, unit: 'g' };
    case 'l':
      return { quantity: quantity * 1000, unit: 'ml' };
    case 'cl':
      return { quantity: quantity * 10, unit: 'ml' };
    default:
      return { quantity, unit };
  }
}

// Categorized food emojis for ingredient suggestions
export const INGREDIENT_EMOJI_CATEGORIES = {
  vegetables: {
    label: { ar: 'خضروات', fr: 'Légumes' },
    emojis: [
      '🍅',
      '🥕',
      '🧅',
      '🧄',
      '🥔',
      '🌽',
      '🥒',
      '🥬',
      '🥦',
      '🍄‍🟫',
      '🫑',
      '🌶️',

      '🍆',
      '🫛',
      '🫘',
      '🌱',
      '🥗',
      '🍠',
    ],
  },
  fruits: {
    label: { ar: 'فواكه', fr: 'Fruits' },
    emojis: [
      '🍇',
      '🍈',
      '🍉',
      '🥑',
      '🍊',
      '🍋',
      '🍌',
      '🍍',
      '🥭',
      '🍎',
      '🍏',
      '🍐',
      '🍑',
      '🍒',
      '🍓',
      '🫐',
      '🥝',
    ],
  },
  meat: {
    label: { ar: 'لحوم', fr: 'Viandes' },
    emojis: ['🐄', '🐮', '🐂', '🐃', '🐔', '🐓', '🐏', '🐑', '🐐', '🦃', '🦆', '🐇'],
  },
  seafood: {
    label: { ar: 'مأكولات بحرية', fr: 'Fruits de mer' },
    emojis: ['🦐', '🦞', '🦀', '🐟', '🐠', '🍣', '🍤', '🦑', '🦪'],
  },
  dairy: {
    label: { ar: 'ألبان', fr: 'Produits laitiers' },
    emojis: ['🥛', '🧀', '🧈', '🥚', '🍳'],
  },
  bakery: {
    label: { ar: 'مخبوزات', fr: 'Boulangerie' },
    emojis: ['🍞', '🥖', '🥨', '🥯', '🥐', '🥞', '🧇'],
  },
  grains: {
    label: { ar: 'حبوب', fr: 'Céréales' },
    emojis: ['🌾', '🍚', '🍙', '🥟', '🍝', '🍜'],
  },
  drinks: {
    label: { ar: 'مشروبات', fr: 'Boissons' },
    emojis: ['🧃', '🥤', '🧋', '☕', '🍵', '🧉', '🥛', '🧊', '💧'],
  },
  spices: {
    label: { ar: 'توابل', fr: 'Épices' },
    emojis: ['🧂', '🫚', '🌶️', '🧄', '🫒'],
  },
  sweets: {
    label: { ar: 'حلويات', fr: 'Desserts' },
    emojis: ['🍯', '🍬', '🍭', '🍮', '🍰', '🎂', '🧁', '🥧', '🍦', '🍨', '🍧', '🍡', '🍩', '🍪'],
  },
  nuts: {
    label: { ar: 'مكسرات', fr: 'Noix' },
    emojis: ['🥜', '🌰', '🫘'],
  },
  other: {
    label: { ar: 'أخرى', fr: 'Autres' },
    emojis: ['🍽️', '🥄', '🔪', '🫙'],
  },
};

// Get all emojis as a flat array
export function getAllFoodEmojis(): string[] {
  return Object.values(INGREDIENT_EMOJI_CATEGORIES).flatMap((category) => category.emojis);
}

// Default emoji
export const DEFAULT_INGREDIENT_EMOJI = '🍽️';
